<template>
  <div class="booking-public-page">
    <!-- Header & Logo Section -->
    <header class="booking-header">
      <div class="container header-container">
        <div class="brand-logo" @click="handleGoHome">
          <div class="logo-icon">
            <i class="fas fa-ticket-alt" />
          </div>
          <div class="brand-text">
            <h1 class="brand-title">TDM BOOKING</h1>
            <span class="brand-subtitle">Đặt vé & Trải nghiệm văn hóa</span>
          </div>
        </div>

        <nav class="header-nav">
          <el-button
            type="text"
            class="nav-link active"
            icon="el-icon-place"
            @click="handleGoHome"
          >
            Khám phá điểm đến
          </el-button>
          <el-button
            type="primary"
            round
            icon="el-icon-tickets"
            class="my-orders-btn"
            @click="handleGoMyOrders"
          >
            Đơn hàng của tôi
          </el-button>
        </nav>
      </div>
    </header>

    <!-- Hero Banner & Date Search Bar Section -->
    <section class="hero-section">
      <div class="hero-overlay" />
      <div class="container hero-content">
        <h2 class="hero-title">Khám Phá Các Điểm Đến & Đặt Vé Trực Tuyến</h2>
        <p class="hero-desc">Lựa chọn ngày khởi hành để tìm các suất trải nghiệm phù hợp nhất</p>

        <!-- Search Bar -->
        <div class="search-card">
          <el-row :gutter="16" type="flex" align="bottom">
            <el-col :xs="24" :sm="14" :md="16">
              <div class="search-item">
                <label class="search-label"><i class="far fa-calendar-alt" /> Chọn ngày khởi hành / tham quan:</label>
                <el-date-picker
                  v-model="selectedDate"
                  type="date"
                  value-format="yyyy-MM-dd"
                  placeholder="Chọn ngày (Ví dụ: 2026-08-20)"
                  style="width: 100%;"
                  clearable
                  @change="handleSearch"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="10" :md="8" class="text-right">
              <div class="search-actions">
                <el-button
                  type="warning"
                  icon="el-icon-search"
                  class="btn-search"
                  :loading="loading"
                  @click="handleSearch"
                >
                  Tìm kiếm suất vé
                </el-button>
                <el-button
                  v-if="selectedDate"
                  type="info"
                  plain
                  icon="el-icon-refresh-left"
                  @click="resetFilter"
                >
                  Xóa lọc
                </el-button>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </section>

    <!-- Places List Section -->
    <main class="container main-content">
      <div class="section-header">
        <div class="section-title-wrap">
          <h3 class="section-title">Danh Sách Điểm Đến Đã Mở Đặt Vé</h3>
          <span class="section-count">
            Hợp lệ: <strong>{{ filteredPlaces.length }}</strong> điểm đến
            <span v-if="selectedDate"> (theo ngày {{ formatDate(selectedDate) }})</span>
          </span>
        </div>
      </div>

      <!-- Loading skeleton / spinner -->
      <div v-if="loading" class="loading-wrap">
        <el-skeleton :rows="6" animated />
      </div>

      <!-- Empty state -->
      <el-empty
        v-else-if="!filteredPlaces.length"
        description="Không tìm thấy điểm đến nào phù hợp với tìm kiếm của bạn."
      >
        <el-button v-if="selectedDate" type="primary" size="small" @click="resetFilter">
          Xem tất cả các điểm đến
        </el-button>
      </el-empty>

      <!-- Grid of Places -->
      <el-row v-else :gutter="24">
        <el-col
          v-for="place in filteredPlaces"
          :key="place.id || place._id"
          :xs="24"
          :sm="12"
          :md="8"
          :lg="8"
          class="mb-4"
        >
          <el-card class="place-card" :body-style="{ padding: '0px' }" shadow="hover">
            <div class="card-image-wrap">
              <img
                :src="getPlaceImage(place)"
                :alt="place.name"
                class="card-image"
              />
              <div v-if="place.product" class="card-badge">
                {{ place.product.type ? place.product.type.toUpperCase() : 'BOOKING' }}
              </div>
            </div>

            <div class="card-body">
              <h4 class="card-title text-truncate" :title="place.name">
                {{ place.name }}
              </h4>

              <p class="card-description">
                {{ getPlaceDescription(place) }}
              </p>

              <div class="card-footer">
                <div class="price-box">
                  <span class="price-label">Giá từ</span>
                  <span class="price-amount">
                    {{ formatCurrency(getPlacePrice(place)) }}
                  </span>
                </div>

                <el-button
                  type="primary"
                  icon="el-icon-date"
                  class="btn-booking"
                  @click="handleGoBooking(place)"
                >
                  Đặt vé ngay
                </el-button>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </main>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listPlaces, getPlaceById } from '@/services/place'
import { getProducts, getCapacities } from '@/services/product'

export default {
  name: 'BookingPublicIndex',
  data() {
    return {
      loading: true,
      selectedDate: '',
      allPlaces: [],
      filteredPlaces: [],
      productMap: {}, // product_id -> product
      mappingList: [] // [{ detail_id, product_id }]
    }
  },
  computed: {
    ...mapGetters(['language'])
  },
  mounted() {
    this.initData()
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      const [y, m, d] = dateStr.split('-')
      return `${d}/${m}/${y}`
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return 'Miễn phí'
      return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val)
    },
    handleGoHome() {
      if (this.$route.path !== '/booking') {
        this.$router.push('/booking')
      }
    },
    handleGoMyOrders() {
      this.$router.push('/booking/me')
    },
    handleGoBooking(place) {
      const placeId = place.id || place._id
      this.$router.push(`/booking/${placeId}`)
    },

    async initData() {
      this.loading = true
      try {
        const storedMapping = localStorage.getItem('shared_booking_products')
        if (storedMapping) {
          try {
            this.mappingList = JSON.parse(storedMapping)
          } catch (e) {
            this.mappingList = []
          }
        }

        const prodRes = await getProducts()
        const prodData = prodRes.data || prodRes || []
        const products = Array.isArray(prodData) ? prodData : []
        products.forEach((p) => {
          const pid = p.id || p._id
          if (pid) {
            this.productMap[pid] = p
          }
        })

        const detailIdsSet = new Set()
        this.mappingList.forEach((m) => {
          if (m.detail_id) detailIdsSet.add(String(m.detail_id))
        })
        products.forEach((p) => {
          if (p.reference_id) detailIdsSet.add(String(p.reference_id))
        })

        let placesList = []
        try {
          const placeRes = await listPlaces({ locale: this.language || 'vi', per_page: 100 })
          placesList = (placeRes && (placeRes.result || placeRes.data)) || []
        } catch (err) {
          console.error('Error fetching listPlaces:', err)
          placesList = []
        }

        const existingIds = new Set(placesList.map((p) => String(p.id || p._id)))
        const missingIds = Array.from(detailIdsSet).filter((id) => !existingIds.has(id))

        if (missingIds.length) {
          const promises = missingIds.map((id) =>
            getPlaceById({ id, locale: this.language || 'vi' }).catch(() => null)
          )
          const fetchedMissing = await Promise.all(promises)
          fetchedMissing.forEach((p) => {
            if (p && (p.id || p._id)) {
              placesList.push(p)
            }
          })
        }

        this.allPlaces = placesList.filter((place) => {
          const pid = place.id || place._id
          const mapping = this.mappingList.find((m) => String(m.detail_id) === String(pid))
          let product = null
          if (mapping && this.productMap[mapping.product_id]) {
            product = this.productMap[mapping.product_id]
          } else {
            product = products.find((p) => String(p.reference_id) === String(pid))
          }
          if (product) {
            place.product = product
            return true
          }
          return false
        })

        this.handleSearch()
      } catch (err) {
        console.error('Error initializing booking data:', err)
      } finally {
        this.loading = false
      }
    },

    async handleSearch() {
      this.loading = true
      try {
        if (!this.selectedDate) {
          this.filteredPlaces = [...this.allPlaces]
          this.loading = false
          return
        }

        const validProductIds = new Set()
        for (const place of this.allPlaces) {
          if (place.product) {
            const prodId = place.product.id || place.product._id
            const capRes = await getCapacities(prodId).catch(() => ({ data: [] }))
            const capacities = capRes.data || capRes || []

            const match = capacities.some((c) => {
              const slotDateStr = c.slot_date ? c.slot_date.substring(0, 10) : (c.date ? String(c.date).substring(0, 10) : '')
              if (slotDateStr && slotDateStr === String(this.selectedDate)) return true
              if (c.start_date && c.end_date) {
                const start = c.start_date.substring(0, 10)
                const end = c.end_date.substring(0, 10)
                return this.selectedDate >= start && this.selectedDate <= end
              }
              if (c.start_date && c.start_date.substring(0, 10) === String(this.selectedDate)) return true
              return false
            })

            if (match || (place.product.capacity_mode === 'unlimited' && !capacities.length)) {
              validProductIds.add(String(prodId))
            }
          }
        }

        this.filteredPlaces = this.allPlaces.filter((place) => {
          const prodId = place.product ? (place.product.id || place.product._id) : null
          return prodId && validProductIds.has(String(prodId))
        })
      } catch (err) {
        console.error('Error searching capacities:', err)
        this.filteredPlaces = [...this.allPlaces]
      } finally {
        this.loading = false
      }
    },

    resetFilter() {
      this.selectedDate = ''
      this.filteredPlaces = [...this.allPlaces]
    },

    getPlaceImage(place) {
      if (place.banner) return place.banner
      if (place.thumbnail) return place.thumbnail
      if (place.image) return place.image
      if (place.image_url) return place.image_url
      return 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?auto=format&fit=crop&w=800&q=80'
    },

    getPlaceDescription(place) {
      const text = place.summary || place.description || place.excerpt || place.content || ''
      const cleaned = text.replace(/<[^>]+>/g, '')
      return cleaned.length > 120 ? cleaned.substring(0, 120) + '...' : (cleaned || 'Trải nghiệm không gian văn hóa độc đáo.')
    },

    getPlacePrice(place) {
      if (place.product && place.product.base_price !== undefined) {
        return place.product.base_price
      }
      return 0
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-public-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

// Header
.booking-header {
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;

  .header-container {
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .brand-logo {
    display: flex;
    align-items: center;
    cursor: pointer;

    .logo-icon {
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 20px;
      margin-right: 12px;
      box-shadow: 0 4px 10px rgba(24, 144, 255, 0.3);
    }

    .brand-title {
      font-size: 20px;
      font-weight: 700;
      color: #1f2937;
      margin: 0;
      line-height: 1.2;
      letter-spacing: 0.5px;
    }

    .brand-subtitle {
      font-size: 11px;
      color: #6b7280;
      font-weight: 500;
    }
  }

  .header-nav {
    display: flex;
    align-items: center;
    gap: 12px;

    .nav-link {
      font-size: 14px;
      font-weight: 600;
      color: #4b5563;

      &:hover, &.active {
        color: #1890ff;
      }
    }

    .my-orders-btn {
      font-weight: 600;
      padding: 9px 20px;
    }
  }
}

// Hero Section
.hero-section {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 60px 0 80px 0;
  color: #ffffff;
  overflow: hidden;

  .hero-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(24, 144, 255, 0.15) 0%, transparent 60%);
  }

  .hero-content {
    position: relative;
    z-index: 2;
    text-align: center;

    .hero-title {
      font-size: 32px;
      font-weight: 800;
      margin: 0 0 10px 0;
      background: linear-gradient(90deg, #ffffff 0%, #e2e8f0 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .hero-desc {
      font-size: 16px;
      color: #94a3b8;
      margin: 0 0 35px 0;
    }
  }
}

// Search Card
.search-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  text-align: left;
  max-width: 800px;
  margin: 0 auto;

  .search-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;

    i {
      color: #1890ff;
      margin-right: 6px;
    }
  }

  .search-actions {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 8px;
  }

  .btn-search {
    font-weight: 600;
    padding: 11px 24px;
  }
}

// Main Content
.main-content {
  padding: 60px 20px 60px 20px;

  .section-header {
    margin-bottom: 28px;
    padding-top: 10px;

    .section-title {
      font-size: 24px;
      font-weight: 800;
      color: #111827;
      margin: 0 0 6px 0;
    }

    .section-count {
      font-size: 14px;
      color: #6b7280;
    }
  }
}

// Place Cards
.place-card {
  border-radius: 12px;
  overflow: hidden;
  border: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
  }

  .card-image-wrap {
    position: relative;
    height: 200px;
    overflow: hidden;
    background-color: #e5e7eb;

    .card-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    .card-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      background: rgba(15, 23, 42, 0.75);
      backdrop-filter: blur(4px);
      color: #38bdf8;
      font-size: 11px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 20px;
      letter-spacing: 0.5px;
    }
  }

  &:hover .card-image {
    transform: scale(1.05);
  }

  .card-body {
    padding: 18px;
    display: flex;
    flex-direction: column;
    flex: 1;

    .card-title {
      font-size: 17px;
      font-weight: 700;
      color: #1f2937;
      margin: 0 0 8px 0;
      line-height: 1.3;
    }

    .card-description {
      font-size: 13px;
      color: #6b7280;
      line-height: 1.5;
      margin: 0 0 16px 0;
      flex: 1;
    }

    .card-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 14px;
      border-top: 1px solid #f3f4f6;

      .price-box {
        display: flex;
        flex-direction: column;

        .price-label {
          font-size: 11px;
          color: #9ca3af;
          text-transform: uppercase;
          font-weight: 600;
        }

        .price-amount {
          font-size: 16px;
          font-weight: 700;
          color: #ef4444;
        }
      }

      .btn-booking {
        font-weight: 600;
        border-radius: 8px;
        padding: 8px 16px;
      }
    }
  }
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loading-wrap {
  padding: 30px;
  background: #ffffff;
  border-radius: 12px;
}
</style>
